// src/components/informatie/ArticlePage.tsx
// Individuele artikel pagina met SEO optimalisatie
// WFT Study Buddy - Content Management System
// MODERN GLASSMORPHISM DESIGN + ANALYTICS + MOBILE TOC + PROGRESS BAR

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import {
  getArticleBySlug,
  Article
} from '../../services/contentService';
import {
  trackArticleView,
  trackArticleFAQExpand,
  trackArticleCTAClick,
  trackArticleTOCClick,
  createScrollDepthTracker,
  createTimeOnPageTracker
} from '../../services/analytics';
import RelatedArticles from './RelatedArticles';

// Category labels
const CategoryLabels: Record<string, string> = {
  examens: 'Examens',
  begrippen: 'Begrippen',
  studietips: 'Studietips',
  statistieken: 'Statistieken',
};

// Category Icons with gradient colors
const CategoryIcons: Record<string, React.ReactNode> = {
  examens: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
  ),
  begrippen: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
    </svg>
  ),
  studietips: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
    </svg>
  ),
  statistieken: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
    </svg>
  ),
};

// Structured Data voor SEO + GEO
const generateArticleSchema = (article: Article) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": article.title,
  "description": article.metaDescription,
  "datePublished": article.publishedAt,
  "dateModified": article.updatedAt,
  "author": {
    "@type": "Organization",
    "name": "WFT Study Buddy",
    "url": "https://wft-study-buddy.nl",
    "sameAs": [
      "https://wft-study-buddy.nl"
    ]
  },
  "publisher": {
    "@type": "Organization",
    "name": "WFT Study Buddy",
    "url": "https://wft-study-buddy.nl",
    "logo": {
      "@type": "ImageObject",
      "url": "https://wft-study-buddy.nl/logo192.png"
    }
  },
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": article.canonicalUrl
  },
  "inLanguage": "nl",
  "isAccessibleForFree": true,
  ...(article.keywords && article.keywords.length > 0 && {
    "keywords": article.keywords.join(', ')
  }),
  ...(article.wordCount && {
    "wordCount": article.wordCount
  }),
  "speakable": {
    "@type": "SpeakableSpecification",
    "cssSelector": [".speakable", ".key-facts", "h1"]
  },
  "about": {
    "@type": "Thing",
    "name": article.h1 || article.title,
    "description": article.metaDescription
  },
  "mentions": [
    { "@type": "Organization", "name": "Autoriteit Financiële Markten (AFM)", "sameAs": "https://www.afm.nl" },
    { "@type": "Organization", "name": "De Nederlandsche Bank (DNB)", "sameAs": "https://www.dnb.nl" },
    { "@type": "Organization", "name": "College Deskundigheid Financiële Dienstverlening (CDFD)", "sameAs": "https://www.cdfd.nl" }
  ],
  "citation": article.citableFacts?.map(f => f.fact) || []
});

// Breadcrumb Schema voor Google
const generateBreadcrumbSchema = (category: string, categoryLabel: string, article: Article) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": "https://wft-study-buddy.nl"
    },
    {
      "@type": "ListItem",
      "position": 2,
      "name": "Informatie",
      "item": "https://wft-study-buddy.nl/informatie"
    },
    {
      "@type": "ListItem",
      "position": 3,
      "name": categoryLabel,
      "item": `https://wft-study-buddy.nl/informatie/${category}`
    },
    {
      "@type": "ListItem",
      "position": 4,
      "name": article.title
    }
  ]
});

// FAQ Schema voor Google
const generateFAQSchema = (article: Article) => {
  if (!article.faqSchema || article.faqSchema.length === 0) return null;
  
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": article.faqSchema.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};

// Interne cross-linking: vervang begrippen in body met links naar andere artikelen
interface LinkableArticle {
  title: string;
  slug: string;
  category: string;
  h1?: string;
}

const injectInternalLinks = (body: string, linkableArticles: LinkableArticle[], currentSlug: string): string => {
  if (!linkableArticles.length) return body;

  let result = body;
  let linksAdded = 0;
  const maxLinks = 5;

  // Sorteer op langste titel eerst om gedeeltelijke matches te voorkomen
  const sorted = [...linkableArticles]
    .filter(a => a.slug !== currentSlug)
    .sort((a, b) => (b.h1 || b.title).length - (a.h1 || a.title).length);

  for (const article of sorted) {
    if (linksAdded >= maxLinks) break;

    // Probeer h1 (korter begrip) eerst, dan title
    const terms = [article.h1, article.title].filter(Boolean) as string[];

    for (const term of terms) {
      // Skip te korte termen (< 4 tekens) om false positives te voorkomen
      if (term.length < 4) continue;

      // Escape regex speciale tekens
      const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      // Match alleen hele woorden, niet in headers of bestaande links
      const regex = new RegExp(`(?<![#\\[/])\\b(${escaped})\\b(?![\\]\\(])`, 'i');
      const match = result.match(regex);

      if (match && match.index !== undefined) {
        // Check dat de match niet in een header-regel of link staat
        const lineStart = result.lastIndexOf('\n', match.index) + 1;
        const line = result.substring(lineStart, match.index + match[0].length + 50);
        if (line.startsWith('#') || line.includes('](')) continue;

        const link = `[${match[0]}](/informatie/${article.category}/${article.slug})`;
        result = result.substring(0, match.index) + link + result.substring(match.index + match[0].length);
        linksAdded++;
        break; // Eén link per artikel
      }
    }
  }

  return result;
};

// Extract headings from markdown for TOC
const extractHeadings = (markdown: string): { id: string; text: string; level: number }[] => {
  const headingRegex = /^(#{2,3})\s+(.+)$/gm;
  const headings: { id: string; text: string; level: number }[] = [];
  let match;
  
  while ((match = headingRegex.exec(markdown)) !== null) {
    const level = match[1].length;
    const text = match[2].trim();
    const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    headings.push({ id, text, level });
  }
  
  return headings;
};

const ArticlePage: React.FC = () => {
  const { category, slug } = useParams<{ category: string; slug: string }>();
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [readingProgress, setReadingProgress] = useState(0);
  const [tocOpen, setTocOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string>('');
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [linkableArticles, setLinkableArticles] = useState<LinkableArticle[]>([]);

  const contentRef = useRef<HTMLDivElement>(null);
  const scrollTrackerRef = useRef<{ start: () => void; stop: () => void } | null>(null);
  const timeTrackerRef = useRef<{ start: () => void; stop: () => void } | null>(null);

  // Track scroll for header styling
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Load article
  useEffect(() => {
    if (slug) {
      loadArticle(slug);
    }
  }, [slug]);

  // Manage JSON-LD schemas with ID-based dedup (prevents duplicates with SSR/prerender)
  useEffect(() => {
    if (!article) return;

    const setJsonLd = (id: string, data: object | null) => {
      if (!data) return;
      let script = document.getElementById(id) as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement('script');
        script.id = id;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(data);
    };

    setJsonLd('ld-article', generateArticleSchema(article));
    setJsonLd('ld-article-breadcrumb', generateBreadcrumbSchema(category || '', CategoryLabels[category || ''] || category || '', article));
    setJsonLd('ld-article-faq', generateFAQSchema(article));

    return () => {
      ['ld-article', 'ld-article-breadcrumb', 'ld-article-faq'].forEach(id => {
        document.getElementById(id)?.remove();
      });
    };
  }, [article, category]);

  // Setup analytics tracking
  useEffect(() => {
    if (article) {
      trackArticleView({
        articleId: article.id,
        articleSlug: article.slug,
        articleTitle: article.title,
        category: article.category,
        wordCount: article.wordCount,
        readTimeMinutes: article.readTimeMinutes,
      });

      scrollTrackerRef.current = createScrollDepthTracker(article.id, article.slug);
      scrollTrackerRef.current.start();

      timeTrackerRef.current = createTimeOnPageTracker(article.id, article.slug);
      timeTrackerRef.current.start();

      return () => {
        scrollTrackerRef.current?.stop();
        timeTrackerRef.current?.stop();
      };
    }
  }, [article]);

  // Reading progress bar
  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return;
      
      const element = contentRef.current;
      const rect = element.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const elementHeight = element.offsetHeight;
      
      const scrolled = Math.max(0, -rect.top);
      const scrollableHeight = elementHeight - windowHeight;
      const progress = Math.min(100, Math.max(0, (scrolled / scrollableHeight) * 100));
      
      setReadingProgress(progress);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [article]);

  // Active section detection for TOC
  useEffect(() => {
    if (!article) return;
    
    const headings = extractHeadings(article.body);
    if (headings.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-100px 0px -66%' }
    );

    headings.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [article]);

  // Fetch linkable articles for internal cross-linking
  useEffect(() => {
    if (!article) return;

    const fetchLinkableArticles = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';
        const response = await fetch(
          `${backendUrl}/api/content/articles?status=published&category=begrippen&limit=100&sortBy=publishedAt&sortOrder=desc`
        );
        if (response.ok) {
          const data = await response.json();
          const articles: LinkableArticle[] = (data.articles || []).map((a: any) => ({
            title: a.title,
            slug: a.slug,
            category: a.category,
            h1: a.h1
          }));
          setLinkableArticles(articles);
        }
      } catch (err) {
        // Silently fail - cross-linking is an enhancement, not critical
      }
    };

    fetchLinkableArticles();
  }, [article]);

  const loadArticle = async (articleSlug: string) => {
    setLoading(true);
    setError(null);

    // Check for pre-rendered article data embedded by build script.
    // This prevents API calls that fail in Google's renderer (causing Soft 404).
    const prerenderEl = document.getElementById('__PRERENDER_DATA__');
    if (prerenderEl) {
      try {
        const prerendered = JSON.parse(prerenderEl.textContent || '');
        if (prerendered && prerendered.slug === articleSlug) {
          setArticle(prerendered);
          setLoading(false);
          prerenderEl.remove(); // Clean up so SPA navigation uses API
          return;
        }
      } catch {
        // Invalid JSON, fall through to API
      }
    }

    try {
      const data = await getArticleBySlug(articleSlug);
      if (!data) {
        setError('Artikel niet gevonden');
      } else {
        setArticle(data);
      }
    } catch (err: any) {
      setError(err.message || 'Fout bij laden artikel');
    } finally {
      setLoading(false);
    }
  };

  // Extract TOC from article body
  const tableOfContents = useMemo(() => {
    if (!article?.body) return [];
    return extractHeadings(article.body);
  }, [article?.body]);

  // Process body with internal cross-links
  const processedBody = useMemo(() => {
    if (!article?.body) return '';
    if (!linkableArticles.length || !slug) return article.body;
    return injectInternalLinks(article.body, linkableArticles, slug);
  }, [article?.body, linkableArticles, slug]);

  const categoryLabel = CategoryLabels[category || ''] || category;
  const categoryIcon = CategoryIcons[category || ''];

  // Handle TOC click with analytics
  const handleTOCClick = (heading: { id: string; text: string }) => {
    if (article) {
      trackArticleTOCClick({
        articleId: article.id,
        sectionId: heading.id,
        sectionTitle: heading.text,
      });
    }
    setTocOpen(false);
  };

  // Handle FAQ expand with analytics
  const handleFAQToggle = (question: string, index: number, isOpen: boolean) => {
    if (isOpen && article) {
      trackArticleFAQExpand({
        articleId: article.id,
        question,
        faqIndex: index,
      });
    }
  };

  // Handle CTA click with analytics
  const handleCTAClick = (ctaType: 'inline' | 'sidebar' | 'bottom' | 'header' | 'related_module', ctaText: string, destination: string) => {
    if (article) {
      trackArticleCTAClick({
        articleId: article.id,
        articleSlug: article.slug,
        ctaType,
        ctaText,
        destination,
      });
    }
  };

  // Canonical URL op basis van route params (beschikbaar voor loading/error states)
  const pageCanonical = `https://wft-study-buddy.nl/informatie/${category}/${slug}`;
  const pageTitle = slug
    ? slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
    : 'Artikel';

  // Loading state
  if (loading) {
    return (
      <>
        <Helmet>
          <title>{pageTitle} | WFT Study Buddy</title>
          <link rel="canonical" href={pageCanonical} />
          <link rel="alternate" hrefLang="nl-NL" href={pageCanonical} />
          <link rel="alternate" hrefLang="x-default" href={pageCanonical} />
        </Helmet>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 animate-pulse">
              <span className="text-white font-bold text-2xl">W</span>
            </div>
            <p className="text-gray-600">Artikel laden...</p>
          </div>
        </div>
      </>
    );
  }

  // Error state
  if (error || !article) {
    return (
      <>
      <Helmet>
        <title>{pageTitle} | WFT Study Buddy</title>
        <link rel="canonical" href={pageCanonical} />
        <link rel="alternate" hrefLang="nl-NL" href={pageCanonical} />
        <link rel="alternate" hrefLang="x-default" href={pageCanonical} />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        {/* Header */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex justify-between items-center">
              <Link to="/" className="flex items-center space-x-2">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-xl">W</span>
                </div>
                <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  WFT Study Buddy
                </span>
              </Link>
            </div>
          </div>
        </nav>
        
        <div className="max-w-4xl mx-auto px-4 pt-32 pb-16">
          <div className="text-center">
            <div className="w-20 h-20 bg-white/60 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
              <svg className="w-10 h-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Artikel niet gevonden</h1>
            <p className="text-gray-600 mb-8">{error || 'Dit artikel bestaat niet of is niet meer beschikbaar.'}</p>
            <Link
              to="/informatie"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg transform hover:scale-105 transition-all"
            >
              ← Terug naar informatie
            </Link>
          </div>
        </div>
      </div>
      </>
    );
  }

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{article.title} | WFT Study Buddy</title>
        <meta name="description" content={article.metaDescription} />
        <meta name="keywords" content={article.keywords?.join(', ')} />
        <link rel="canonical" href={article.canonicalUrl || `https://wft-study-buddy.nl/informatie/${category}/${slug}`} />

        {/* Hreflang */}
        <link rel="alternate" hrefLang="nl-NL" href={article.canonicalUrl || `https://wft-study-buddy.nl/informatie/${category}/${slug}`} />
        <link rel="alternate" hrefLang="x-default" href={article.canonicalUrl || `https://wft-study-buddy.nl/informatie/${category}/${slug}`} />

        {/* Open Graph */}
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={article.canonicalUrl} />
        <meta property="og:locale" content="nl_NL" />
        <meta property="og:site_name" content="WFT Study Buddy" />

        {/* Article-specific OG tags */}
        {article.publishedAt && <meta property="article:published_time" content={new Date(article.publishedAt).toISOString()} />}
        {article.updatedAt && <meta property="article:modified_time" content={new Date(article.updatedAt).toISOString()} />}
        <meta property="article:author" content="WFT Study Buddy" />
        {category && <meta property="article:section" content={CategoryLabels[category] || category} />}

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={article.metaDescription} />
      </Helmet>

      {/* Reading Progress Bar */}
      <div 
        className="fixed top-0 left-0 h-1 bg-gradient-to-r from-blue-500 to-purple-600 z-[60] transition-all duration-150"
        style={{ width: `${readingProgress}%` }}
      />

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 relative">
        {/* Decorative Background Blobs */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse" />
          <div className="absolute top-40 right-10 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute bottom-40 left-1/3 w-72 h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-15" />
        </div>

        {/* Main Header - Consistent with Homepage */}
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white/90 backdrop-blur-lg shadow-lg py-3' : 'bg-white/60 backdrop-blur-sm py-4'
        }`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center">
              {/* Logo */}
              <Link to="/" className="flex items-center space-x-2 group">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center transform group-hover:scale-105 transition-transform">
                  <span className="text-white font-bold text-xl">W</span>
                </div>
                <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  WFT Study Buddy
                </span>
              </Link>

              {/* Desktop Navigation */}
              <div className="hidden md:flex items-center space-x-6">
                <Link to="/#features" className="text-gray-600 hover:text-blue-600 transition">Features</Link>
                <Link to="/#how-it-works" className="text-gray-600 hover:text-blue-600 transition">Hoe werkt het?</Link>
                <Link to="/#pricing" className="text-gray-600 hover:text-blue-600 transition">Prijzen</Link>
                <Link to="/informatie" className="text-blue-600 font-medium">Informatie</Link>
                <Link to="/login" className="text-gray-600 hover:text-blue-600 transition">Inloggen</Link>
                <Link 
                  to="/register" 
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-5 py-2 rounded-lg hover:shadow-lg transform hover:scale-105 transition-all"
                >
                  Registreren
                </Link>
              </div>

              {/* Mobile Menu Button */}
              <button 
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden text-gray-600 hover:text-blue-600 transition-colors p-2"
                aria-label="Menu"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isMobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden bg-white/95 backdrop-blur-lg border-t border-gray-100 shadow-lg">
              <div className="px-4 py-4 space-y-3">
                <Link to="/" className="block py-2 text-gray-600 hover:text-blue-600">Home</Link>
                <Link to="/#features" className="block py-2 text-gray-600 hover:text-blue-600">Features</Link>
                <Link to="/#pricing" className="block py-2 text-gray-600 hover:text-blue-600">Prijzen</Link>
                <Link to="/informatie" className="block py-2 text-blue-600 font-medium">Informatie</Link>
                <hr className="border-gray-200" />
                <Link to="/login" className="block py-2 text-gray-600 hover:text-blue-600">Inloggen</Link>
                <Link 
                  to="/register" 
                  className="block w-full text-center bg-gradient-to-r from-blue-600 to-purple-600 text-white px-5 py-2.5 rounded-lg"
                >
                  Registreren
                </Link>
              </div>
            </div>
          )}
        </nav>

        {/* Mobile TOC Toggle */}
        {tableOfContents.length > 0 && (
          <div className="lg:hidden fixed top-[60px] left-0 right-0 z-40 bg-white/90 backdrop-blur-lg border-b border-gray-200/50">
            <button
              onClick={() => setTocOpen(!tocOpen)}
              className="w-full px-4 py-3 flex items-center justify-between text-sm font-medium text-gray-700"
            >
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.007v.008H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-.375 5.25h.007v.008H3.75v-.008zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                </svg>
                Inhoudsopgave
              </span>
              <svg 
                className={`w-4 h-4 text-gray-400 transition-transform ${tocOpen ? 'rotate-180' : ''}`} 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {/* Mobile TOC Dropdown */}
            {tocOpen && (
              <nav className="px-4 pb-4 space-y-1 max-h-64 overflow-y-auto bg-white/95">
                {tableOfContents.map((heading) => (
                  <a
                    key={heading.id}
                    href={`#${heading.id}`}
                    onClick={() => handleTOCClick(heading)}
                    className={`block py-2 text-sm transition-colors rounded-lg px-3 ${
                      heading.level === 3 ? 'pl-6' : ''
                    } ${
                      activeSection === heading.id
                        ? 'bg-blue-50 text-blue-600 font-medium'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    {heading.text}
                  </a>
                ))}
              </nav>
            )}
          </div>
        )}

        {/* Article Content */}
        <main className={`relative z-10 max-w-6xl mx-auto px-4 ${tableOfContents.length > 0 ? 'pt-28 lg:pt-28' : 'pt-24'} pb-16`} ref={contentRef}>
          {/* Breadcrumb */}
          <nav className="mb-6">
            <div className="flex items-center gap-2 text-sm">
              <Link to="/" className="text-gray-500 hover:text-blue-600 transition">Home</Link>
              <span className="text-gray-300">/</span>
              <Link to="/informatie" className="text-gray-500 hover:text-blue-600 transition">Informatie</Link>
              <span className="text-gray-300">/</span>
              <Link to={`/informatie/${category}`} className="text-gray-500 hover:text-blue-600 transition">
                {categoryLabel}
              </Link>
            </div>
          </nav>

          <div className="lg:grid lg:grid-cols-[1fr_280px] lg:gap-8">
            {/* Main Content */}
            <article>
              {/* Article Header Card */}
              <header className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 shadow-lg p-6 sm:p-8 mb-8">
                {/* Category Badge */}
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500/10 to-purple-500/10 text-blue-700 text-sm font-semibold rounded-xl border border-blue-200/50">
                    {categoryIcon}
                    {categoryLabel}
                  </span>
                  <span className="text-sm text-gray-500 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {article.readTimeMinutes} min leestijd
                  </span>
                </div>

                {/* H1 Title */}
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 leading-tight mb-4">
                  {article.h1 || article.title}
                </h1>

                {/* Meta Info */}
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                  <span className="flex items-center gap-1.5">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                    </svg>
                    {article.wordCount} woorden
                  </span>
                </div>
              </header>

              {/* Intro Card */}
              {article.intro && (
                <div className="mb-8 p-6 bg-gradient-to-br from-blue-500/5 to-purple-500/5 backdrop-blur-sm rounded-2xl border border-blue-200/30">
                  <p className="text-lg text-gray-700 leading-relaxed">
                    {article.intro}
                  </p>
                </div>
              )}

              {/* Body Content Card */}
              <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 shadow-lg p-6 sm:p-8 mb-8">
                <div className="prose prose-lg prose-gray max-w-none">
                  <ReactMarkdown
                    components={{
                      h2: ({ children }) => {
                        const text = String(children);
                        const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
                        return (
                          <h2 id={id} className="text-2xl font-bold text-gray-900 mt-10 mb-4 first:mt-0 scroll-mt-32 pb-2 border-b border-gray-100">
                            {children}
                          </h2>
                        );
                      },
                      h3: ({ children }) => {
                        const text = String(children);
                        const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
                        return (
                          <h3 id={id} className="text-xl font-semibold text-gray-900 mt-8 mb-3 scroll-mt-32">
                            {children}
                          </h3>
                        );
                      },
                      p: ({ children }) => (
                        <p className="text-gray-700 leading-relaxed mb-4">{children}</p>
                      ),
                      ul: ({ children }) => (
                        <ul className="list-disc list-outside ml-5 space-y-2 mb-4 text-gray-700">{children}</ul>
                      ),
                      ol: ({ children }) => (
                        <ol className="list-decimal list-outside ml-5 space-y-2 mb-4 text-gray-700">{children}</ol>
                      ),
                      li: ({ children }) => (
                        <li className="text-gray-700">{children}</li>
                      ),
                      blockquote: ({ children }) => (
                        <blockquote className="border-l-4 border-blue-400 bg-blue-50/50 pl-4 py-2 italic text-gray-600 my-6 rounded-r-lg">
                          {children}
                        </blockquote>
                      ),
                      a: ({ href, children }) => {
                        // Internal links (from cross-linking) use React Router
                        if (href && href.startsWith('/')) {
                          return (
                            <Link to={href} className="text-blue-600 hover:text-blue-800 underline decoration-blue-300 hover:decoration-blue-500 transition-colors">
                              {children}
                            </Link>
                          );
                        }
                        return (
                          <a href={href} className="text-blue-600 hover:text-blue-800 underline decoration-blue-300 hover:decoration-blue-500 transition-colors" target="_blank" rel="noopener noreferrer">
                            {children}
                          </a>
                        );
                      },
                      strong: ({ children }) => (
                        <strong className="font-semibold text-gray-900">{children}</strong>
                      ),
                    }}
                  >
                    {processedBody}
                  </ReactMarkdown>
                </div>



              </div>

              {/* Key Facts Section (GEO: zichtbare citeerbare feiten) */}
              {article.citableFacts && article.citableFacts.length > 0 && (
                <section className="key-facts mb-8">
                  <div className="bg-gradient-to-br from-emerald-50 to-teal-50 backdrop-blur-sm rounded-2xl border border-emerald-200/50 shadow-lg p-6 sm:p-8">
                    <div className="flex items-center gap-3 mb-5">
                      <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />
                        </svg>
                      </div>
                      <h2 className="text-xl font-bold text-gray-900">Belangrijkste feiten</h2>
                    </div>
                    <ul className="space-y-3">
                      {article.citableFacts.map((item, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <span className="flex-shrink-0 w-6 h-6 bg-emerald-200 text-emerald-800 rounded-full flex items-center justify-center text-xs font-bold mt-0.5">
                            {index + 1}
                          </span>
                          <span className="text-gray-700 leading-relaxed">{item.fact}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="mt-4 text-xs text-emerald-600/70">
                      Bron: CDFD Toetskader &amp; WFT regelgeving
                    </p>
                  </div>
                </section>
              )}

              {/* Speakable Sections (hidden maar met speakable class voor schema) */}
              {article.speakableSections && article.speakableSections.length > 0 && (
                <div className="speakable sr-only" aria-hidden="true">
                  {article.speakableSections.map((section, index) => (
                    <p key={index}>{section}</p>
                  ))}
                </div>
              )}

              {/* FAQ Section */}
              {article.faqSchema && article.faqSchema.length > 0 && (
                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Veelgestelde vragen</h2>
                  <div className="space-y-3">
                    {article.faqSchema.map((faq, index) => (
                      <details
                        key={index}
                        className="group bg-white/70 backdrop-blur-sm rounded-xl border border-white/50 shadow-sm overflow-hidden"
                        onToggle={(e) => handleFAQToggle(faq.question, index, (e.target as HTMLDetailsElement).open)}
                      >
                        <summary className="flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-white/80 transition-colors">
                          <span className="font-medium text-gray-900 pr-4">{faq.question}</span>
                          <svg
                            className="w-5 h-5 text-blue-500 transition-transform group-open:rotate-180 flex-shrink-0"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </summary>
                        <div className="px-6 pb-4 text-gray-600 border-t border-gray-100 pt-4">
                          {faq.answer}
                        </div>
                      </details>
                    ))}
                  </div>
                </section>
              )}

              {/* Related Modules */}
              {article.relatedModules && article.relatedModules.length > 0 && (
                <section className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Gerelateerde WFT modules</h3>
                  <div className="flex flex-wrap gap-2">
                    {article.relatedModules.map((module) => (
                      <Link
                        key={module}
                        to={`/wft-${module}`}
                        onClick={() => handleCTAClick('related_module', module, `/wft-${module}`)}
                        className="px-4 py-2 bg-white/70 backdrop-blur-sm text-gray-700 border border-white/50 rounded-xl hover:bg-white hover:shadow-md hover:border-blue-200 transition-all text-sm"
                      >
                        {module.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </Link>
                    ))}
                  </div>
                </section>
              )}

              {/* Internal Linking Components */}
{/* <InternalLinkSuggestions 
  examType={article.relatedModules?.[0]}
  relatedModules={article.relatedModules}
/> */}

<RelatedArticles
  currentArticleId={article.id}
  category={article.category}
  examType={article.relatedModules?.[0]}
  keywords={article.keywords}
  limit={4}
/>

              {/* CTA Section */}
              <section className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl p-8 text-center shadow-xl">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                  Klaar om te oefenen?
                </h2>
                <p className="text-blue-100 mb-6 max-w-md mx-auto">
                  Start vandaag nog met oefenen en verhoog je slagingskans voor je WFT examen.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Link
                    to="/app"
                    onClick={() => handleCTAClick('bottom', 'Start gratis oefenen', '/app')}
                    className="px-6 py-3 bg-white text-blue-600 rounded-xl font-semibold hover:bg-blue-50 hover:shadow-lg transform hover:scale-105 transition-all"
                  >
                    Start gratis oefenen
                  </Link>
                  <Link
                    to="/informatie"
                    onClick={() => handleCTAClick('bottom', 'Meer artikelen', '/informatie')}
                    className="px-6 py-3 bg-white/10 text-white border border-white/30 rounded-xl font-semibold hover:bg-white/20 transition-all"
                  >
                    Meer artikelen
                  </Link>
                </div>
              </section>
            </article>

            {/* Desktop Sidebar TOC */}
            {tableOfContents.length > 0 && (
              <aside className="hidden lg:block">
                <div className="sticky top-28">
                  <nav className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 shadow-lg p-5">
                    <h4 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <svg className="w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.007v.008H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-.375 5.25h.007v.008H3.75v-.008zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                      </svg>
                      Inhoudsopgave
                    </h4>
                    <div className="space-y-1">
                      {tableOfContents.map((heading) => (
                        <a
                          key={heading.id}
                          href={`#${heading.id}`}
                          onClick={() => handleTOCClick(heading)}
                          className={`block py-2 text-sm rounded-lg transition-all ${
                            heading.level === 3 ? 'pl-5' : 'pl-3'
                          } ${
                            activeSection === heading.id
                              ? 'bg-blue-50 text-blue-600 font-medium border-l-2 border-blue-500'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 border-l-2 border-transparent'
                          }`}
                        >
                          {heading.text}
                        </a>
                      ))}
                    </div>
                  </nav>

                  {/* Sidebar CTA */}
                  <div className="mt-6 bg-gradient-to-br from-blue-500/10 to-purple-500/10 backdrop-blur-sm rounded-2xl border border-blue-200/30 p-5">
                    <h4 className="font-semibold text-gray-900 mb-2">Direct oefenen?</h4>
                    <p className="text-sm text-gray-600 mb-4">Probeer onze AI-gegenereerde oefenvragen.</p>
                    <Link
                      to="/app"
                      onClick={() => handleCTAClick('sidebar', 'Start nu', '/app')}
                      className="block w-full text-center px-4 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-medium hover:shadow-lg transform hover:scale-[1.02] transition-all text-sm"
                    >
                      Start nu →
                    </Link>
                  </div>
                </div>
              </aside>
            )}
          </div>
        </main>

        {/* Scroll to Top Button */}
        {readingProgress > 20 && (
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="fixed bottom-6 right-6 w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 text-white rounded-full shadow-lg flex items-center justify-center hover:shadow-xl transform hover:scale-110 transition-all z-40"
            aria-label="Scroll naar boven"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 10l7-7m0 0l7 7m-7-7v18" />
            </svg>
          </button>
        )}

        {/* Footer */}
        <footer className="relative z-10 border-t border-white/30 bg-white/60 backdrop-blur-sm mt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {/* Brand */}
              <div className="col-span-1 md:col-span-2">
                <Link to="/" className="flex items-center space-x-2 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-xl">W</span>
                  </div>
                  <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    WFT Study Buddy
                  </span>
                </Link>
                <p className="text-gray-600 text-sm max-w-md">
                  Bereid je optimaal voor op je WFT examen met AI-gegenereerde oefenvragen en complete samenvattingen.
                </p>
              </div>

              {/* Links */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-4">Navigatie</h4>
                <nav className="space-y-2">
                  <Link to="/informatie" className="block text-sm text-gray-600 hover:text-blue-600 transition">Informatie</Link>
                  <Link to="/app" className="block text-sm text-gray-600 hover:text-blue-600 transition">Oefenen</Link>
                  <Link to="/#pricing" className="block text-sm text-gray-600 hover:text-blue-600 transition">Prijzen</Link>
                </nav>
              </div>

              {/* Legal */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-4">Juridisch</h4>
                <nav className="space-y-2">
                  <Link to="/privacy" className="block text-sm text-gray-600 hover:text-blue-600 transition">Privacy</Link>
                  <Link to="/terms" className="block text-sm text-gray-600 hover:text-blue-600 transition">Voorwaarden</Link>
                </nav>
              </div>
            </div>

            <div className="border-t border-gray-200 mt-8 pt-8 text-center text-sm text-gray-500" suppressHydrationWarning>
              © {new Date().getFullYear()} WFT Study Buddy. Alle rechten voorbehouden.
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ArticlePage;